import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimWorkOrderPriceData, BimWorkOrderTotalItemData } from 'gql/graphql';
import { BimChapterEnum, BimPriceLevelEnum } from 'gql/graphql';
import { isNotNil } from 'utils';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderTotalsRow } from '../bimWorkOrderQueries';
import { BimWorkOrderRowTypeEnum, getDetailsInPriceTableRequestId } from '../types';

export const useBimWorkOrderTotalsRow = ({
    workOrderId,
    constructionTypeId,
    chapter,
    workOrderObjectId,
    useInPrices = true,
    checkedPriceRowForDetails,
    withEffect = true,
    resetFilterData,
    priceLevel,
}: {
    workOrderId: number;
    constructionTypeId: number | undefined;
    chapter: BimChapterEnum | undefined;
    workOrderObjectId: number | undefined;
    useInPrices?: boolean;
    checkedPriceRowForDetails?: {
        rowType: BimWorkOrderRowTypeEnum;
        data: BimWorkOrderTotalItemData | BimWorkOrderPriceData | null;
    } | null;
    withEffect?: boolean;
    resetFilterData?: () => void;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const dispatch = useDispatch();

    const isTotalsDetails = useMemo(
        () => !!checkedPriceRowForDetails && checkedPriceRowForDetails.rowType === BimWorkOrderRowTypeEnum.TotalsString,
        [checkedPriceRowForDetails],
    );

    const priceLevelByChapter = useMemo(() => {
        if (chapter === BimChapterEnum.Others) {
            return BimPriceLevelEnum.BasicPriceLevel;
        }

        return priceLevel;
    }, [chapter, priceLevel]);

    const totalsRowVars = useMemo(
        () => ({
            workOrderId,
            constructionTypeId,
            chapter,
            workOrderObjectId,
            useInPrices,
            priceLevel: priceLevelByChapter,
        }),
        [chapter, constructionTypeId, priceLevelByChapter, useInPrices, workOrderId, workOrderObjectId],
    );

    const isRequestLegit = useMemo(() => {
        const variablesFull =
            Object.values(totalsRowVars).every((key) => isNotNil(key)) && totalsRowVars.workOrderId === workOrderId;

        if (isTotalsDetails && !useInPrices) {
            return variablesFull && isTotalsDetails;
        }

        if (useInPrices) {
            return variablesFull;
        }

        return false;
    }, [isTotalsDetails, totalsRowVars, useInPrices, workOrderId]);

    const requestId = getDetailsInPriceTableRequestId(useInPrices);

    const [makeResetFetchedData] = useResetStoreData({
        requestId,
    });

    useEffect(() => {
        makeResetFetchedData(undefined);
    }, [makeResetFetchedData, totalsRowVars]);

    const [rowTotals, rowTotalsDataStatus, , rowTotalsDataRequest] = useRequest({
        requestId,
        query: bimWorkOrderTotalsRow,
        variables: totalsRowVars,
        withEffect: isRequestLegit && withEffect,
    });

    const makeRefreshDetailsData = useCallback(() => {
        dispatch(rowTotalsDataRequest(totalsRowVars));

        if (resetFilterData) {
            resetFilterData();
        }
    }, [dispatch, resetFilterData, rowTotalsDataRequest, totalsRowVars]);

    const isLoading = rowTotalsDataStatus === RequestStatus.FETCHING;

    const rowTotalsData = useMemo(
        () =>
            rowTotals?.bimWorkOrderTotals?.items?.map((i) => ({
                ...i,
                planValueOthers: i.others,
                planValueTotal: i.total,
                isTotalsRow: true,
            })) ?? [],
        [rowTotals?.bimWorkOrderTotals?.items],
    );

    return {
        rowTotalsData,
        isLoading,
        makeRefreshDetailsData,
    };
};
